import {
    DAYCARE_DURATION_OPTIONS,
    LATE_PICKUP_FEE_TYPES,
    DaycareDurationOption,
    LatePickupFeeType
} from 'model/Service';

// Type definitions for UI components
export interface DurationOptionWithLabel {
    key: DaycareDurationOption;
    label: string;
    value: DaycareDurationOption;
}

export interface LatePickupFeeOption {
    value: LatePickupFeeType;
    label: string;
    description: string;
}

export interface RadioGroupLatePickupOption {
    value: LatePickupFeeType;
    inlineLabel: string;
    description: undefined;
}

export interface RadioGroupDurationOption {
    value: DaycareDurationOption;
    inlineLabel: string;
    description: undefined;
}

// UI-specific constants with labels and descriptions
export const DAYCARE_DURATION_OPTIONS_WITH_LABELS = [
    {
        key: DAYCARE_DURATION_OPTIONS.HOURLY,
        label: 'Hourly',
        value: DAYCARE_DURATION_OPTIONS.HOURLY
    },
    {
        key: DAYCARE_DURATION_OPTIONS.HALF_DAY,
        label: 'Half Day',
        value: DAYCARE_DURATION_OPTIONS.HALF_DAY
    },
    {
        key: DAYCARE_DURATION_OPTIONS.FULL_DAY,
        label: 'Full Day',
        value: DAYCARE_DURATION_OPTIONS.FULL_DAY
    }
] as const;

// Late pickup fee options with UI labels and descriptions
export const LATE_PICKUP_FEE_OPTIONS = {
    [LATE_PICKUP_FEE_TYPES.PER_HOUR]: {
        value: LATE_PICKUP_FEE_TYPES.PER_HOUR,
        label: 'Per Hour',
        description: 'Set an additional hourly rate if a pet stays longer.'
    },
    [LATE_PICKUP_FEE_TYPES.ADDITIONAL_HALF_DAY]: {
        value: LATE_PICKUP_FEE_TYPES.ADDITIONAL_HALF_DAY,
        label: 'Additional Half Day',
        description: 'Set an additional half day rate if a pet stays longer.'
    },
    [LATE_PICKUP_FEE_TYPES.ADDITIONAL_FULL_DAY]: {
        value: LATE_PICKUP_FEE_TYPES.ADDITIONAL_FULL_DAY,
        label: 'Additional Full Day',
        description: 'Set an additional full day rate if a pet stays longer.'
    }
} as const;

// UI Helper functions
export const getLatePickupFeeOptionsForDuration = (duration: DaycareDurationOption): LatePickupFeeOption[] => {
    const baseOptions: LatePickupFeeOption[] = [
        LATE_PICKUP_FEE_OPTIONS[LATE_PICKUP_FEE_TYPES.PER_HOUR],
        LATE_PICKUP_FEE_OPTIONS[LATE_PICKUP_FEE_TYPES.ADDITIONAL_HALF_DAY]
    ];

    // Add "Additional Full Day" only for Full Day duration
    if (duration === DAYCARE_DURATION_OPTIONS.FULL_DAY) {
        return [...baseOptions, LATE_PICKUP_FEE_OPTIONS[LATE_PICKUP_FEE_TYPES.ADDITIONAL_FULL_DAY]];
    }

    return baseOptions;
};

// Helper to get price unit text for duration
export const getDurationPriceUnit = (duration: DaycareDurationOption): string => {
    switch (duration) {
        case DAYCARE_DURATION_OPTIONS.HOURLY:
            return 'per hour';
        case DAYCARE_DURATION_OPTIONS.HALF_DAY:
            return 'per half day';
        case DAYCARE_DURATION_OPTIONS.FULL_DAY:
            return 'per full day';
        default:
            return 'per hour';
    }
};
